import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingService } from '../../services/api/billingService';
import type { Invoice, PaginatedResult } from '../../types/billing';
import './BillingHistoryPage.css';

export function BillingHistoryPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<PaginatedResult<Invoice> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadInvoices();
  }, [page]);

  const loadInvoices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await billingService.getInvoices(page, 10);
      setInvoices(result);
    } catch (e) {
      setError('Failed to load billing history');
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      Paid: 'paid',
      Open: 'open',
      Draft: 'draft',
      Void: 'void',
      Uncollectible: 'uncollectible',
    };
    return statusClasses[status] || 'default';
  };

  if (isLoading) {
    return (
      <div className="billing-history-page">
        <div className="loading">Loading billing history...</div>
      </div>
    );
  }

  return (
    <div className="billing-history-page">
      <header className="history-header">
        <button className="back-button" onClick={() => navigate('/billing')}>
          &larr; Back to Billing
        </button>
        <h1>Billing History</h1>
      </header>

      {error && (
        <div className="history-error">
          <p>{error}</p>
          <button onClick={loadInvoices}>Try Again</button>
        </div>
      )}

      {invoices && invoices.items.length === 0 ? (
        <div className="history-empty">
          <div className="empty-icon">&#128196;</div>
          <h3>No Billing History</h3>
          <p>You don't have any invoices yet.</p>
        </div>
      ) : (
        <>
          <div className="invoices-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices?.items.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="invoice-number">
                      {invoice.invoiceNumber || `INV-${invoice.id}`}
                    </td>
                    <td>{formatDate(invoice.createdAt)}</td>
                    <td>
                      {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                    </td>
                    <td className="invoice-amount">
                      {formatPrice(invoice.amount, invoice.currency)}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      {invoice.pdfUrl && (
                        <a
                          href={invoice.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-download"
                        >
                          Download PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invoices && invoices.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={!invoices.hasPreviousPage}
              >
                &larr; Previous
              </button>
              <span>
                Page {invoices.page} of {invoices.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={!invoices.hasNextPage}
              >
                Next &rarr;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
